import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../../../api/client';
import { TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, PRIMARY, FONT_HEADING, BG_CARD } from './designTokens';
import { EntranceDirectionIndicator } from './EntranceDirectionIndicator';

// ─── Types ────────────────────────────────────────────────────────────────────

interface GridTable {
  id: string;
  tableNumber: number;
  tableName: string;
  capacity: number;
  status: string;
  zone: { name: string; colorCode: string };
}

interface GridSlot {
  id: string;
  gridAddress: string;
  section: string;
  rowSetKey: string;
  rowPosition: string;
  column: string;
  columnIndex: number;
  tableId: string | null;
  table: GridTable | null;
}

interface RowSet {
  id: string;
  key: string;
  name: string;
  description: string | null;
  colorCode: string | null;
  sortOrder: number;
  slots: GridSlot[];
}

interface UnassignedTable {
  id: string;
  tableNumber: number;
  tableName: string;
  capacity: number;
  status: string;
  zone: { name: string; colorCode: string };
}

interface FloorplanGridViewProps {
  onSelectTable: (tableId: string) => void;
  refreshKey: number;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const COLUMN_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  W: { bg: '#1565c0', border: '#0d47a1', text: '#fff' },
  C: { bg: '#2e7d32', border: '#1b5e20', text: '#fff' },
  A: { bg: '#f9a825', border: '#f57f17', text: '#333' },
};

const styles: Record<string, React.CSSProperties> = {
  container: { position: 'relative' },
  infographic: {
    background: '#f0f4f2',
    borderRadius: '12px',
    padding: '1.5rem 1rem',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  title: { textAlign: 'center', color: TEXT_DARK, fontFamily: FONT_HEADING, fontSize: '1rem', marginBottom: '0.2rem' },
  subtitle: { textAlign: 'center', color: TEXT_MUTED, fontSize: '0.72rem', marginBottom: '1.2rem' },
  stage: {
    background: 'linear-gradient(135deg, #2d2d2d, #444)',
    borderRadius: '8px',
    padding: '0.75rem',
    textAlign: 'center',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.85rem',
    marginBottom: '1rem',
    border: '2px solid #555',
  },
  floor: { display: 'grid', gridTemplateColumns: '1fr 50px 1fr', gap: '0' },
  section: { display: 'flex', flexDirection: 'column', gap: '4px' },
  walkway: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'repeating-linear-gradient(0deg, #e8eae9 0px, #e8eae9 8px, #f0f2f1 8px, #f0f2f1 16px)',
    borderLeft: '2px dashed #c8d0ca',
    borderRight: '2px dashed #c8d0ca',
  },
  walkwayLabel: { writingMode: 'vertical-rl', color: TEXT_MUTED, fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' },
  sectionLabel: { textAlign: 'center', fontSize: '0.65rem', fontWeight: 700, color: TEXT_DARK, padding: '3px 0', textTransform: 'uppercase', letterSpacing: '0.08em' },
  rowSet: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '8px',
    padding: '8px 6px',
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  rowSetTag: { fontSize: '0.95rem', color: TEXT_DARK, fontWeight: 800, padding: '0.4rem 4px 0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: FONT_HEADING },
  row: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' },
  entrance: {
    background: 'linear-gradient(135deg, #e8eae9, #d0d8d3)',
    borderRadius: '8px',
    padding: '0.6rem',
    textAlign: 'center',
    color: '#333',
    fontWeight: 700,
    fontSize: '0.8rem',
    marginTop: '1rem',
    border: '2px solid #aab',
  },
  legend: { display: 'flex', justifyContent: 'center', gap: '1.2rem', marginTop: '1rem', flexWrap: 'wrap' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.7rem', color: TEXT_MUTED },
  legendDot: { width: '12px', height: '12px', borderRadius: '50%' },
  count: { textAlign: 'center', color: TEXT_MUTED, fontSize: '0.75rem', marginTop: '0.75rem', fontWeight: 600 },
  // Assignment modal
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: BG_CARD,
    borderRadius: '8px',
    padding: '1.25rem',
    width: '360px',
    maxWidth: '90vw',
    maxHeight: '70vh',
    overflowY: 'auto',
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  },
  modalTitle: { fontSize: '0.9rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '0.75rem' },
  tableOption: {
    padding: '0.5rem 0.6rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    cursor: 'pointer',
    fontSize: '0.8rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'background 0.15s',
  },
  // Edit row-set name
  editInput: {
    background: '#fff',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '3px',
    color: TEXT_DARK,
    fontSize: '0.58rem',
    padding: '1px 4px',
    width: '90px',
    outline: 'none',
  },
};

const ROWSET_TINTS: Record<string, string> = {
  '1': 'rgba(139, 26, 43, 0.12)',
  '2': 'rgba(21, 101, 192, 0.12)',
  '3': 'rgba(106, 27, 154, 0.12)',
  '4': 'rgba(84, 110, 122, 0.12)',
};

// ─── Component ────────────────────────────────────────────────────────────────

export function FloorplanGridView({ onSelectTable, refreshKey }: FloorplanGridViewProps) {
  const [rowSets, setRowSets] = useState<RowSet[]>([]);
  const [unassignedTables, setUnassignedTables] = useState<UnassignedTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Assignment modal state
  const [assigningSlot, setAssigningSlot] = useState<GridSlot | null>(null);
  // Hover tooltip state
  const [hoverSlot, setHoverSlot] = useState<{ slot: GridSlot; rect: DOMRect } | null>(null);
  const [hoverGuests, setHoverGuests] = useState<Array<{ fullName: string; ticketNumber: string | null; orderId: string | null; familyAssociation: string | null; chapter: string | null }>>([]);
  const [hoverLoading, setHoverLoading] = useState(false);
  const hoverCacheRef = React.useRef<Map<string, typeof hoverGuests>>(new Map());
  const hoverTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Double-tap detection for mobile
  const lastTapRef = React.useRef<{ id: string; time: number }>({ id: '', time: 0 });
  // Action popup for assigned slots
  const [actionSlot, setActionSlot] = useState<GridSlot | null>(null);

  // Edit row-set name state
  const [showRowSetModal, setShowRowSetModal] = useState(false);
  const [rowSetEdits, setRowSetEdits] = useState<Record<string, { name: string; description: string; colorCode: string }>>({});

  // Drag and drop state
  const [dragTableId, setDragTableId] = useState<string | null>(null);
  const [dragFromSlot, setDragFromSlot] = useState<string | null>(null); // gridAddress of source slot (for moves)
  const [dropTarget, setDropTarget] = useState<string | null>(null); // gridAddress being hovered
  const [showSidebar, setShowSidebar] = useState(true);
  const [bandOpacity, setBandOpacity] = useState(0.2);
  const [showDirections, setShowDirections] = useState(false);

  // President tables highlight
  const [presidentTableIds, setPresidentTableIds] = useState<Set<string>>(new Set());
  const [presidentNames, setPresidentNames] = useState<Set<string>>(new Set());

  // Table search (by number or name) — highlights matching slots
  const [searchTerm, setSearchTerm] = useState('');
  const matchedTableIds = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return new Set<string>();
    const ids = new Set<string>();
    for (const rs of rowSets) {
      for (const slot of rs.slots) {
        const t = slot.table;
        if (!t) continue;
        const numMatch = String(t.tableNumber) === q || `t${t.tableNumber}`.toLowerCase() === q || String(t.tableNumber).includes(q);
        const nameMatch = t.tableName?.toLowerCase().includes(q);
        if (numMatch || nameMatch) ids.add(t.id);
      }
    }
    return ids;
  }, [searchTerm, rowSets]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [gridData, tablesData, presidentData] = await Promise.all([
        apiClient<RowSet[]>('/floorplan-grid').catch(() => [] as RowSet[]),
        apiClient<UnassignedTable[]>('/floorplan-grid/unassigned-tables').catch(() => [] as UnassignedTable[]),
        apiClient<{ tableIds: string[]; presidentNames: string[] }>('/floorplan-grid/president-tables').catch(() => ({ tableIds: [], presidentNames: [] })),
      ]);
      setRowSets(Array.isArray(gridData) ? gridData : []);
      setUnassignedTables(Array.isArray(tablesData) ? tablesData : []);
      setPresidentTableIds(new Set(presidentData.tableIds || []));
      setPresidentNames(new Set((presidentData.presidentNames || []).map((n: string) => n.toLowerCase())));
    } catch {
      setError('Failed to load grid layout. The grid tables may not be initialized yet.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (refreshKey > 0) fetchData(); }, [refreshKey, fetchData]);

  // Assign table to slot
  const handleAssign = async (tableId: string) => {
    if (!assigningSlot) return;
    try {
      await apiClient(`/floorplan-grid/slots/${assigningSlot.gridAddress}/assign`, {
        method: 'PUT',
        body: { tableId },
      });
      setAssigningSlot(null);
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to assign table.');
    }
  };

  // Handle drop on a grid slot
  const handleDrop = async (targetGridAddress: string) => {
    if (!dragTableId) return;
    setDropTarget(null);
    try {
      // If moving from another slot, unassign first
      if (dragFromSlot) {
        await apiClient(`/floorplan-grid/slots/${dragFromSlot}/assign`, { method: 'DELETE' });
      }
      await apiClient(`/floorplan-grid/slots/${targetGridAddress}/assign`, {
        method: 'PUT',
        body: { tableId: dragTableId },
      });
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to assign table.');
    } finally {
      setDragTableId(null);
      setDragFromSlot(null);
    }
  };

  // Unassign table from slot
  const handleUnassign = async (slot: GridSlot) => {
    try {
      await apiClient(`/floorplan-grid/slots/${slot.gridAddress}/assign`, { method: 'DELETE' });
      await fetchData();
    } catch (err: any) {
      setError(err?.message || 'Failed to unassign.');
    }
  };

  // Render a single table circle
  const renderSlot = (slot: GridSlot) => {
    const colColor = COLUMN_COLORS[slot.column] || COLUMN_COLORS.C;
    const isAssigned = !!slot.table || !!slot.tableId;
    const isVip = isAssigned && slot.table?.zone?.name?.toLowerCase() === 'vip';
    const hasPresident = isAssigned && slot.table ? presidentTableIds.has(slot.table.id) : false;
    const isMatch = isAssigned && slot.table ? matchedTableIds.has(slot.table.id) : false;

    return (
      <div
        key={slot.id}
        data-grid-slot="true"
        onClick={(e) => {
          const now = Date.now();
          const lastTap = lastTapRef.current;
          const isDoubleTap = lastTap.id === slot.id && now - lastTap.time < 350;
          lastTapRef.current = { id: slot.id, time: now };

          if (isDoubleTap) {
            // Double tap: open action popup or assign modal
            setHoverSlot(null);
            setHoverGuests([]);
            if (isAssigned) {
              setActionSlot(slot);
            } else {
              setAssigningSlot(slot);
            }
          } else {
            // Single tap: show popover (like hover on desktop)
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            setHoverSlot({ slot, rect });
            if (slot.table) {
              const cached = hoverCacheRef.current.get(slot.table.id);
              if (cached) {
                setHoverGuests(cached);
              } else {
                setHoverLoading(true);
                setTimeout(async () => {
                  try {
                    const data = await apiClient<any>(`/table-management/tables/${slot.table!.id}`);
                    const guests = (data.seats || [])
                      .filter((s: any) => s.guestName)
                      .map((s: any) => ({ fullName: s.guestName || '', ticketNumber: s.guestTicketNumber || null, orderId: null, familyAssociation: s.guestFamily || null, chapter: s.guestChapter || null }));
                    hoverCacheRef.current.set(slot.table!.id, guests);
                    setHoverGuests(guests);
                  } catch { setHoverGuests([]); }
                  finally { setHoverLoading(false); }
                }, 100);
              }
            }
          }
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          if (isAssigned) setActionSlot(slot);
        }}
        draggable={isAssigned}
        onDragStart={(e) => {
          if (isAssigned && slot.table) {
            e.dataTransfer.setData('text/plain', slot.table.id);
            setDragTableId(slot.table.id);
            setDragFromSlot(slot.gridAddress);
          } else if (isAssigned && slot.tableId) {
            e.dataTransfer.setData('text/plain', slot.tableId);
            setDragTableId(slot.tableId);
            setDragFromSlot(slot.gridAddress);
          }
        }}
        onDragOver={(e) => {
          if (!isAssigned && dragTableId) {
            e.preventDefault();
            setDropTarget(slot.gridAddress);
          }
        }}
        onDragLeave={() => setDropTarget(null)}
        onDrop={(e) => {
          e.preventDefault();
          if (!isAssigned) {
            handleDrop(slot.gridAddress);
          }
        }}
        onMouseEnter={(e) => {
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          setHoverSlot({ slot, rect });
          // Fetch guests if table is assigned
          if (slot.table) {
            const cached = hoverCacheRef.current.get(slot.table.id);
            if (cached) {
              setHoverGuests(cached);
            } else {
              setHoverLoading(true);
              if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
              hoverTimerRef.current = setTimeout(async () => {
                try {
                  const data = await apiClient<any>(`/table-management/tables/${slot.table!.id}`);
                  const guests = (data.seats || [])
                    .filter((s: any) => s.guestName)
                    .map((s: any) => ({
                      fullName: s.guestName || '',
                      ticketNumber: s.guestTicketNumber || null,
                      orderId: null,
                      familyAssociation: s.guestFamily || null,
                      chapter: s.guestChapter || null,
                    }));
                  hoverCacheRef.current.set(slot.table!.id, guests);
                  setHoverGuests(guests);
                } catch {
                  setHoverGuests([]);
                } finally {
                  setHoverLoading(false);
                }
              }, 150);
            }
          } else {
            setHoverGuests([]);
          }
        }}
        onMouseLeave={() => {
          setHoverSlot(null);
          setHoverGuests([]);
          setHoverLoading(false);
          if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
        }}
        style={{
          width: '100%',
          aspectRatio: '1',
          borderRadius: '50%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.55rem',
          fontWeight: 700,
          color: isAssigned ? (isVip ? '#fff' : colColor.text) : '#666',
          backgroundColor: isVip ? '#b71c1c' : isAssigned ? colColor.bg : dropTarget === slot.gridAddress ? 'rgba(21,101,192,0.1)' : 'transparent',
          border: isMatch ? '3px solid #06b6d4' : isVip ? '4px solid #e53935' : isAssigned ? `2px solid ${colColor.border}` : dropTarget === slot.gridAddress ? '2px solid #1565c0' : '2px dashed #bbb',
          cursor: 'pointer',
          transition: 'transform 0.15s, box-shadow 0.15s',
          boxShadow: isMatch
            ? '0 0 0 4px rgba(6, 182, 212, 0.45), 0 0 18px rgba(6, 182, 212, 0.9)'
            : isVip ? '0 0 16px rgba(229, 57, 53, 0.8), inset 0 0 8px rgba(229, 57, 53, 0.3)' : isAssigned ? `0 2px 8px ${colColor.bg}66` : 'none',
          transform: isMatch ? 'scale(1.12)' : undefined,
          zIndex: isMatch ? 5 : undefined,
          minWidth: '34px',
          minHeight: '34px',
          position: 'relative',
        }}
        title={`${slot.gridAddress}${slot.table ? ` → T${slot.table.tableNumber}` : ' (click to assign)'}`}
        aria-label={`Grid ${slot.gridAddress}${slot.table ? `, Table ${slot.table.tableNumber}` : ', unassigned'}`}
      >
        {isAssigned ? (
          <>
            <span style={{ fontSize: '0.7rem', lineHeight: 1, color: hasPresident ? '#ffd600' : undefined, fontWeight: hasPresident ? 900 : 700, textShadow: hasPresident ? '0 0 4px rgba(255,214,0,0.8)' : undefined }}>{slot.table ? `T${slot.table.tableNumber}` : '✓'}</span>
            <span style={{ fontSize: '0.45rem', opacity: 0.8, marginTop: '1px' }}>{slot.gridAddress.split('-').pop()}</span>
          </>
        ) : (
          <span style={{ fontSize: '0.5rem', color: '#666' }}>{slot.gridAddress.split('-').pop()}</span>
        )}
      </div>
    );
  };

  // Render a row-set for one section
  const renderRowSet = (rowSet: RowSet, section: 'L' | 'R') => {
    const sectionSlots = rowSet.slots.filter((s) => s.section === section);
    // Convert hex to rgba with visible opacity for band background
    let tint: string;
    if (rowSet.colorCode) {
      const hex = rowSet.colorCode.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      tint = `rgba(${r}, ${g}, ${b}, ${bandOpacity})`;
    } else {
      // Use default color with user-controlled opacity
      const defaults: Record<string, [number, number, number]> = { '1': [139, 26, 43], '2': [21, 101, 192], '3': [106, 27, 154], '4': [84, 110, 122] };
      const [r, g, b] = defaults[rowSet.key] || [150, 150, 150];
      tint = `rgba(${r}, ${g}, ${b}, ${bandOpacity})`;
    }

    // Group slots by rowPosition
    const positions = [...new Set(sectionSlots.map((s) => s.rowPosition))].sort();

    // For row-set 1, the grid is 7 cols but some A columns are empty (dance floor)
    // We render all slots that exist; empty positions are absent from data
    const getRowSlots = (pos: string) => {
      const rowSlots = sectionSlots.filter((s) => s.rowPosition === pos);
      // Build a 7-slot array: W3, W1, W2, C1, C2, A1, A2 for Left; A1, A2, C1, C2, W1, W2, W3 for Right
      const order = section === 'L'
        ? [['W', 3], ['W', 1], ['W', 2], ['C', 1], ['C', 2], ['A', 1], ['A', 2]]
        : [['A', 1], ['A', 2], ['C', 1], ['C', 2], ['W', 1], ['W', 2], ['W', 3]];

      return order.map(([col, idx]) => {
        const slot = rowSlots.find((s) => s.column === col && s.columnIndex === idx);
        return slot || null;
      });
    };

    return (
      <div key={`${rowSet.key}-${section}`} style={{ ...styles.rowSet, background: tint, ...(rowSet.colorCode ? { borderColor: rowSet.colorCode, borderWidth: '1.5px', borderStyle: 'solid' } : {}) }}>
        <div style={styles.rowSetTag as React.CSSProperties}>
          {section === 'L' && (
            <span style={{ cursor: 'default' }} title={rowSet.description || ''}>{rowSet.name}</span>
          )}
          {section === 'R' && <span title={rowSet.description || ''}>{rowSet.name}</span>}
        </div>
        {positions.map((pos) => {
          const slots = getRowSlots(pos);
          return (
            <div key={pos} style={styles.row}>
              {slots.map((slot, idx) =>
                slot ? renderSlot(slot) : (
                  <div
                    key={`empty-${idx}`}
                    style={{
                      width: '100%',
                      aspectRatio: '1',
                      borderRadius: '50%',
                      border: '2px dashed #d0d8d3',
                      minWidth: '34px',
                      minHeight: '34px',
                    }}
                  />
                ),
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Count assigned
  const totalSlots = rowSets.reduce((sum, rs) => sum + rs.slots.length, 0);
  const assignedSlots = rowSets.reduce((sum, rs) => sum + rs.slots.filter((s) => s.tableId).length, 0);

  // Generate human-readable directions for a slot
  const getDirections = (slot: GridSlot, rowSet: RowSet): string => {
    const sideLabel = slot.section === 'L' ? 'left' : 'right';
    const colLabels: Record<string, string> = { W: 'against the wall', C: 'in the center section', A: 'closest to the main walkway' };
    const colLabel = colLabels[slot.column] || '';
    const rowLabels: Record<string, string> = { '1': '1st (front, near stage)', '2': '2nd', '3': '3rd', '4': '4th (back, near entrance)' };
    const rowLabel = rowLabels[slot.rowSetKey] || '';
    const posLabel = slot.rowPosition === 'A' ? 'front row of the set' : slot.rowPosition === 'B' ? 'back row of the set' : '';

    const entrances = [
      { key: 'salon_a', label: 'Salon A', pos: 0 },
      { key: 'salon_b', label: 'Salon B', pos: 1 },
      { key: 'main', label: 'Main Entrance', pos: 2 },
      { key: 'salon_d', label: 'Salon D', pos: 3 },
      { key: 'salon_e', label: 'Salon E', pos: 4 },
    ];
    const lateral = slot.section === 'L' ? (slot.column === 'W' ? 0 : slot.column === 'C' ? 1 : 2) : (slot.column === 'A' ? 2 : slot.column === 'C' ? 3 : 4);
    const best = entrances.reduce((a, b) => Math.abs(lateral - a.pos) <= Math.abs(lateral - b.pos) ? a : b);

    return `Enter via ${best.label}. Your table is on the ${sideLabel} side, ${rowLabel} row-set (${rowSet.name}), ${posLabel}, ${colLabel}. Grid: ${slot.gridAddress}`;
  };

  // Get suggested entrance label for a slot
  const getSuggestedEntrance = (slot: GridSlot): string => {
    const entrances = [
      { label: 'Salon A', pos: 0 },
      { label: 'Salon B', pos: 1 },
      { label: 'Main Entrance', pos: 2 },
      { label: 'Salon D', pos: 3 },
      { label: 'Salon E', pos: 4 },
    ];
    const lateral = slot.section === 'L' ? (slot.column === 'W' ? 0 : slot.column === 'C' ? 1 : 2) : (slot.column === 'A' ? 2 : slot.column === 'C' ? 3 : 4);
    return entrances.reduce((a, b) => Math.abs(lateral - a.pos) <= Math.abs(lateral - b.pos) ? a : b).label;
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: TEXT_MUTED, fontSize: '0.85rem' }}>Loading grid layout...</div>;
  }

  if (error && rowSets.length === 0) {
    return (
      <div style={{ color: '#b71c1c', backgroundColor: '#fdecea', padding: '0.75rem 1rem', borderRadius: '4px', fontSize: '0.85rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} role="alert">
        <span>{error}</span>
        <button style={{ padding: '0.35rem 0.75rem', backgroundColor: '#c62828', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer' }} onClick={fetchData}>Retry</button>
      </div>
    );
  }

  if (rowSets.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: TEXT_MUTED, fontSize: '0.85rem' }}>
        Grid layout not initialized yet. The database migration needs to run first.
      </div>
    );
  }

  return (
    <div style={styles.container} onClick={(e) => { if ((e.target as HTMLElement).closest('[data-grid-slot]') === null) { setHoverSlot(null); setHoverGuests([]); } }}>
      {/* Row-Set Names Button + Sidebar Toggle */}
      {rowSets.length > 0 && (
        <div style={{ marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={() => setShowSidebar(!showSidebar)}
            style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: showSidebar ? PRIMARY : BG_CARD, color: showSidebar ? '#fff' : TEXT_DARK, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', cursor: 'pointer' }}
          >
            {showSidebar ? '◀ Hide Tables' : '▶ Show Tables'} ({unassignedTables.length})
          </button>
          {/* Table search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', marginLeft: '0.75rem', flex: 1, maxWidth: '260px' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.75rem', opacity: 0.5, pointerEvents: 'none' }}>🔍</span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Find table (number or name)"
                aria-label="Search tables by number or name"
                style={{ width: '100%', padding: '0.35rem 1.6rem 0.35rem 1.75rem', fontSize: '0.78rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '6px', outline: 'none', boxSizing: 'border-box' }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear table search"
                  style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: TEXT_MUTED, lineHeight: 1, padding: '2px 4px' }}
                >✕</button>
              )}
            </div>
            {searchTerm.trim() && (
              <span style={{ fontSize: '0.7rem', fontWeight: 600, color: matchedTableIds.size > 0 ? '#0891b2' : '#b91c1c', whiteSpace: 'nowrap' }}>
                {matchedTableIds.size} match{matchedTableIds.size !== 1 ? 'es' : ''}
              </span>
            )}
          </div>
          {/* Band opacity slider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginLeft: '1rem' }}>
            <span style={{ fontSize: '0.7rem', color: TEXT_MUTED }}>Band:</span>
            <input
              type="range"
              min="0"
              max="50"
              value={Math.round(bandOpacity * 100)}
              onChange={(e) => setBandOpacity(Number(e.target.value) / 100)}
              style={{ width: '80px', cursor: 'pointer' }}
              aria-label="Band opacity"
            />
            <span style={{ fontSize: '0.65rem', color: TEXT_MUTED, width: '28px' }}>{Math.round(bandOpacity * 100)}%</span>
          </div>
          <button
            onClick={() => {
              const edits: Record<string, { name: string; description: string; colorCode: string }> = {};
              rowSets.forEach((rs) => { edits[rs.key] = { name: rs.name, description: rs.description || '', colorCode: rs.colorCode || '#8b9a90' }; });
              setRowSetEdits(edits);
              setShowRowSetModal(true);
            }}
            style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: BG_CARD, color: TEXT_DARK, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', cursor: 'pointer' }}
            aria-label="Edit row-set names"
          >
            ⚙️ Row-Set Names
          </button>
          <button
            onClick={() => {
              const gridEl = document.querySelector('[data-floorplan-grid]');
              if (!gridEl) return;
              const printWindow = window.open('', '_blank');
              if (!printWindow) return;
              printWindow.document.write(`<!DOCTYPE html><html><head><title>Floorplan - Grand Ballroom</title><style>
                body { margin: 0; padding: 20px; }
                * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
                @media print { body { padding: 10px; } }
              </style></head><body>${gridEl.outerHTML}</body></html>`);
              printWindow.document.close();
              printWindow.focus();
              setTimeout(() => printWindow.print(), 400);
            }}
            style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: BG_CARD, color: TEXT_DARK, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', cursor: 'pointer', marginLeft: '0.5rem' }}
            aria-label="Print floorplan"
          >
            🖨️ Print
          </button>
          <button
            onClick={() => setShowDirections(!showDirections)}
            style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: showDirections ? PRIMARY : BG_CARD, color: showDirections ? '#fff' : TEXT_DARK, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', cursor: 'pointer', marginLeft: '0.5rem' }}
            aria-label="Toggle directions panel"
          >
            🧭 Directions
          </button>
          <button
            onClick={() => { hoverCacheRef.current.clear(); fetchData(); }}
            style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: BG_CARD, color: TEXT_DARK, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', cursor: 'pointer', marginLeft: '0.5rem' }}
            aria-label="Refresh floorplan"
          >
            🔄 Refresh
          </button>
        </div>
      )}

      {/* Row-Set Names Modal */}
      {showRowSetModal && (
        <div style={styles.modalOverlay as React.CSSProperties} onClick={() => setShowRowSetModal(false)}>
          <div style={{ ...styles.modal as React.CSSProperties, width: '720px' }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Edit row-set names">
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '1rem' }}>Edit Row-Set Names</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${BORDER_LIGHT}` }}>
                  <th style={{ textAlign: 'left', padding: '0.4rem', color: TEXT_MUTED, fontWeight: 600, width: '60px' }}>Row-Set</th>
                  <th style={{ textAlign: 'left', padding: '0.4rem', color: TEXT_MUTED, fontWeight: 600, width: '40px' }}>Color</th>
                  <th style={{ textAlign: 'left', padding: '0.4rem', color: TEXT_MUTED, fontWeight: 600, width: '130px' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '0.4rem', color: TEXT_MUTED, fontWeight: 600 }}>Description / Guidance</th>
                </tr>
              </thead>
              <tbody>
                {rowSets.map((rs) => (
                  <tr key={rs.key} style={{ borderBottom: `1px solid ${BORDER_LIGHT}`, verticalAlign: 'top' }}>
                    <td style={{ padding: '0.5rem 0.4rem', fontWeight: 600, color: TEXT_DARK }}>#{rs.key}</td>
                    <td style={{ padding: '0.5rem 0.4rem' }}>
                      <input
                        type="color"
                        value={rowSetEdits[rs.key]?.colorCode || '#8b9a90'}
                        onChange={(e) => setRowSetEdits((prev) => ({ ...prev, [rs.key]: { ...prev[rs.key], colorCode: e.target.value } }))}
                        style={{ width: '28px', height: '28px', border: 'none', borderRadius: '4px', cursor: 'pointer', padding: 0 }}
                        aria-label={`Color for row-set ${rs.key}`}
                      />
                    </td>
                    <td style={{ padding: '0.5rem 0.4rem' }}>
                      <input
                        style={{ width: '100%', padding: '0.3rem 0.5rem', fontSize: '0.82rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', outline: 'none', color: TEXT_DARK }}
                        value={rowSetEdits[rs.key]?.name || ''}
                        onChange={(e) => setRowSetEdits((prev) => ({ ...prev, [rs.key]: { ...prev[rs.key], name: e.target.value } }))}
                        aria-label={`Name for row-set ${rs.key}`}
                      />
                    </td>
                    <td style={{ padding: '0.5rem 0.4rem' }}>
                      <textarea
                        style={{ width: '100%', padding: '0.3rem 0.5rem', fontSize: '0.78rem', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px', outline: 'none', color: TEXT_DARK, resize: 'vertical', minHeight: '50px', lineHeight: '1.4', fontFamily: 'inherit' }}
                        value={rowSetEdits[rs.key]?.description || ''}
                        onChange={(e) => setRowSetEdits((prev) => ({ ...prev, [rs.key]: { ...prev[rs.key], description: e.target.value } }))}
                        placeholder="Guidance for seating planners..."
                        aria-label={`Description for row-set ${rs.key}`}
                        rows={2}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {error && <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#d32f2f' }}>{error}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button
                onClick={() => setShowRowSetModal(false)}
                style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setError('');
                  try {
                    for (const rs of rowSets) {
                      const edit = rowSetEdits[rs.key];
                      if (!edit) continue;
                      const newName = (edit.name || '').trim();
                      const newDesc = (edit.description || '').trim();
                      const newColor = (edit.colorCode || '').trim();
                      const nameChanged = newName && newName !== rs.name;
                      const descChanged = newDesc !== (rs.description || '');
                      const colorChanged = newColor !== (rs.colorCode || '');
                      if (nameChanged || descChanged || colorChanged) {
                        await apiClient(`/floorplan-grid/row-sets/${rs.key}`, {
                          method: 'PATCH',
                          body: { name: newName || rs.name, description: newDesc, colorCode: newColor },
                        });
                      }
                    }
                    setShowRowSetModal(false);
                    await fetchData();
                  } catch (err: any) {
                    setError(err?.message || 'Failed to save row-set names.');
                  }
                }}
                style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Save All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Directions Panel (expandable) */}
      {showDirections && (
        <div style={{ marginBottom: '0.75rem', backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', padding: '0.75rem', maxHeight: '300px', overflowY: 'auto' }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '0.5rem' }}>🧭 Table Directions ({assignedSlots} tables)</div>
          {rowSets.map((rs) =>
            rs.slots.filter((s) => s.table || s.tableId).map((slot) => (
              <div key={slot.id} style={{ padding: '0.35rem 0.5rem', marginBottom: '3px', backgroundColor: '#f9fafb', borderRadius: '4px', borderLeft: `3px solid ${rs.colorCode || '#999'}`, fontSize: '0.72rem', lineHeight: 1.5 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                  <strong style={{ color: TEXT_DARK }}>T{slot.table?.tableNumber || '?'} — {slot.table?.tableName || slot.gridAddress}</strong>
                  <EntranceDirectionIndicator suggestedEntrance={getSuggestedEntrance(slot)} size="sm" />
                </div>
                <div style={{ color: TEXT_MUTED, marginTop: '2px' }}>{getDirections(slot, rs)}</div>
              </div>
            )),
          )}
          {assignedSlots === 0 && <div style={{ color: TEXT_MUTED, fontSize: '0.78rem' }}>No tables assigned yet.</div>}
        </div>
      )}

      {/* Main content: sidebar + grid */}
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        {/* Unassigned Tables Sidebar */}
        {showSidebar && (
          <div
            style={{ width: '180px', flexShrink: 0, backgroundColor: BG_CARD, border: `1px solid ${BORDER_LIGHT}`, borderRadius: '8px', padding: '0.5rem', maxHeight: '600px', overflowY: 'auto' }}
            onDragOver={(e) => { if (dragFromSlot) e.preventDefault(); }}
            onDrop={async (e) => {
              e.preventDefault();
              // Dropping back to sidebar = unassign
              if (dragFromSlot) {
                try {
                  await apiClient(`/floorplan-grid/slots/${dragFromSlot}/assign`, { method: 'DELETE' });
                  await fetchData();
                } catch {}
                setDragTableId(null);
                setDragFromSlot(null);
              }
            }}
          >
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: TEXT_MUTED, textTransform: 'uppercase', marginBottom: '0.4rem', padding: '0 0.3rem' }}>
              Unassigned ({unassignedTables.length})
            </div>
            {unassignedTables.length === 0 && (
              <div style={{ fontSize: '0.72rem', color: TEXT_MUTED, padding: '0.5rem 0.3rem' }}>All tables assigned</div>
            )}
            {(() => {
              // Group by zone
              const byZone: Record<string, typeof unassignedTables> = {};
              for (const t of unassignedTables) {
                const zoneName = t.zone?.name || 'Unzoned';
                if (!byZone[zoneName]) byZone[zoneName] = [];
                byZone[zoneName].push(t);
              }
              return Object.entries(byZone).map(([zoneName, tables]) => (
                <div key={zoneName} style={{ marginBottom: '0.5rem' }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, color: tables[0]?.zone?.colorCode || TEXT_MUTED, padding: '0.25rem 0.3rem', borderBottom: `1px solid ${BORDER_LIGHT}`, marginBottom: '3px', textTransform: 'uppercase' }}>
                    {zoneName} ({tables.length})
                  </div>
                  {tables.map((t) => (
                    <div
                      key={t.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', t.id);
                        setDragTableId(t.id);
                        setDragFromSlot(null);
                      }}
                      onDragEnd={() => { setDragTableId(null); setDropTarget(null); }}
                      style={{
                        padding: '0.3rem 0.4rem',
                        marginBottom: '2px',
                        backgroundColor: '#f9fafb',
                        border: `1px solid ${BORDER_LIGHT}`,
                        borderLeft: `3px solid ${t.zone?.colorCode || '#999'}`,
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        cursor: 'grab',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.3rem',
                      }}
                    >
                      <span style={{ fontWeight: 700, color: TEXT_DARK }}>T{t.tableNumber}</span>
                      <span style={{ color: TEXT_MUTED, fontSize: '0.62rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.tableName}</span>
                    </div>
                  ))}
                </div>
              ));
            })()}
          </div>
        )}

        {/* Grid Infographic */}
        <div data-floorplan-grid="true" style={{ ...styles.infographic, flex: 1 }}>
        <div style={styles.title as React.CSSProperties}>Grand Ballroom Grid</div>
        <div style={styles.subtitle as React.CSSProperties}>{assignedSlots}/{totalSlots} tables assigned | Click to assign, right-click to unassign</div>

        {/* Stage */}
        <div style={styles.stage as React.CSSProperties}>🎵 STAGE &amp; DANCE FLOOR</div>

        {/* Main Floor Grid */}
        <div style={styles.floor}>
          {/* Left Section */}
          <div style={styles.section as React.CSSProperties}>
            <div style={styles.sectionLabel as React.CSSProperties}>Left (L)</div>
            {rowSets.map((rs) => renderRowSet(rs, 'L'))}
          </div>

          {/* Walkway */}
          <div style={styles.walkway as React.CSSProperties}>
            <span style={styles.walkwayLabel as React.CSSProperties}>Walkway</span>
          </div>

          {/* Right Section */}
          <div style={styles.section as React.CSSProperties}>
            <div style={styles.sectionLabel as React.CSSProperties}>Right (R)</div>
            {rowSets.map((rs) => renderRowSet(rs, 'R'))}
          </div>
        </div>

        {/* Entrance — 5 Salon Doors (double-door icons) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px', marginTop: '1rem' }}>
          {[
            { label: 'Salon A', short: 'A', isMain: false },
            { label: 'Salon B', short: 'B', isMain: false },
            { label: 'Main Entrance', short: 'Main', isMain: true },
            { label: 'Salon D', short: 'D', isMain: false },
            { label: 'Salon E', short: 'E', isMain: false },
          ].map((door) => (
            <div key={door.label} style={{
              background: door.isMain ? 'linear-gradient(135deg, #1a6b3c, #25D366)' : 'linear-gradient(135deg, #e8eae9, #d0d8d3)',
              borderRadius: '8px',
              padding: '0.5rem 0.3rem',
              textAlign: 'center',
              color: door.isMain ? '#fff' : '#333',
              fontWeight: 700,
              fontSize: '0.7rem',
              border: door.isMain ? '2px solid #166534' : '2px solid #aab',
            }}>
              {/* Double door icon */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: door.isMain ? '2px' : '1px', marginBottom: '3px' }}>
                {door.isMain ? (
                  // 4 panels for main entrance
                  <>
                    <div style={{ width: '8px', height: '14px', backgroundColor: door.isMain ? 'rgba(255,255,255,0.3)' : '#999', borderRadius: '2px', border: '1px solid rgba(255,255,255,0.5)' }} />
                    <div style={{ width: '8px', height: '14px', backgroundColor: door.isMain ? 'rgba(255,255,255,0.3)' : '#999', borderRadius: '2px', border: '1px solid rgba(255,255,255,0.5)' }} />
                    <div style={{ width: '8px', height: '14px', backgroundColor: door.isMain ? 'rgba(255,255,255,0.3)' : '#999', borderRadius: '2px', border: '1px solid rgba(255,255,255,0.5)' }} />
                    <div style={{ width: '8px', height: '14px', backgroundColor: door.isMain ? 'rgba(255,255,255,0.3)' : '#999', borderRadius: '2px', border: '1px solid rgba(255,255,255,0.5)' }} />
                  </>
                ) : (
                  // 2 panels for salon doors
                  <>
                    <div style={{ width: '10px', height: '14px', backgroundColor: '#888', borderRadius: '2px', border: '1px solid #666' }} />
                    <div style={{ width: '10px', height: '14px', backgroundColor: '#888', borderRadius: '2px', border: '1px solid #666' }} />
                  </>
                )}
              </div>
              {door.label}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div style={styles.legend as React.CSSProperties}>
          <div style={styles.legendItem}><div style={{ ...styles.legendDot, background: '#1565c0' }} /> Wall (W)</div>
          <div style={styles.legendItem}><div style={{ ...styles.legendDot, background: '#2e7d32' }} /> Center (C)</div>
          <div style={styles.legendItem}><div style={{ ...styles.legendDot, background: '#f9a825' }} /> Aisle (A)</div>
          <div style={styles.legendItem}><div style={{ ...styles.legendDot, border: '2px dashed #bbb', background: 'transparent' }} /> Unassigned</div>
        </div>

        <div style={styles.count as React.CSSProperties}>{assignedSlots} of {totalSlots} slots assigned</div>
      </div>
      </div>{/* end flex wrapper */}

      {/* Hover Tooltip */}
      {hoverSlot && (
        <div
          style={{
            position: 'fixed',
            left: Math.min(hoverSlot.rect.left + hoverSlot.rect.width / 2, window.innerWidth - 240),
            top: hoverSlot.rect.top - 8,
            transform: 'translate(-50%, -100%)',
            backgroundColor: '#fff',
            color: TEXT_DARK,
            padding: '0.6rem 0.8rem',
            borderRadius: '8px',
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 100,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            border: `1px solid ${BORDER_LIGHT}`,
            maxWidth: '480px',
            minWidth: '200px',
          }}
        >
          {/* Zone - large */}
          {hoverSlot.slot.table?.zone && (
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: hoverSlot.slot.table.zone.colorCode || PRIMARY, marginBottom: '2px' }}>
              {hoverSlot.slot.table.zone.name}
            </div>
          )}
          {/* Table name */}
          <div style={{ fontSize: '0.78rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '4px' }}>
            {hoverSlot.slot.table
              ? `${hoverSlot.slot.table.tableName} (T${hoverSlot.slot.table.tableNumber})`
              : hoverSlot.slot.tableId
                ? `Assigned • ${hoverSlot.slot.gridAddress}`
                : hoverSlot.slot.gridAddress}
          </div>
          {/* Guest table */}
          {hoverSlot.slot.table && (
            <>
              {hoverLoading && <div style={{ fontSize: '0.68rem', color: TEXT_MUTED }}>Loading guests...</div>}
              {!hoverLoading && hoverGuests.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.65rem', marginTop: '4px', whiteSpace: 'normal' }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${BORDER_LIGHT}` }}>
                      <th style={{ textAlign: 'left', padding: '2px 3px', color: TEXT_MUTED, fontWeight: 600 }}>Guest</th>
                      <th style={{ textAlign: 'left', padding: '2px 3px', color: TEXT_MUTED, fontWeight: 600 }}>Ticket</th>
                      <th style={{ textAlign: 'left', padding: '2px 3px', color: TEXT_MUTED, fontWeight: 600 }}>Chapter</th>
                      <th style={{ textAlign: 'left', padding: '2px 3px', color: TEXT_MUTED, fontWeight: 600 }}>Order</th>
                      <th style={{ textAlign: 'left', padding: '2px 3px', color: TEXT_MUTED, fontWeight: 600 }}>Family</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hoverGuests.slice(0, 10).map((g, i) => {
                      const isPresident = presidentNames.has(g.fullName.toLowerCase());
                      return (
                      <tr key={i} style={{ borderBottom: `1px solid ${BORDER_LIGHT}`, backgroundColor: isPresident ? 'rgba(229,57,53,0.12)' : undefined }}>
                        <td style={{ padding: '2px 3px', fontWeight: isPresident ? 800 : 500, color: isPresident ? '#c62828' : undefined }}>{g.fullName}{isPresident ? ' 👑' : ''}</td>
                        <td style={{ padding: '2px 3px' }}>{g.ticketNumber || '—'}</td>
                        <td style={{ padding: '2px 3px' }}>{g.chapter || '—'}</td>
                        <td style={{ padding: '2px 3px' }}>{g.orderId || '—'}</td>
                        <td style={{ padding: '2px 3px' }}>{g.familyAssociation || '—'}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {!hoverLoading && hoverGuests.length === 0 && (
                <div style={{ fontSize: '0.65rem', color: TEXT_MUTED, marginTop: '2px' }}>No guests seated</div>
              )}
            </>
          )}
        </div>
      )}

      {/* Assignment Modal */}
      {assigningSlot && (
        <div style={styles.modalOverlay as React.CSSProperties} onClick={() => setAssigningSlot(null)}>
          <div style={styles.modal as React.CSSProperties} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div style={styles.modalTitle}>Assign Table to {assigningSlot.gridAddress}</div>
            <p style={{ fontSize: '0.78rem', color: TEXT_MUTED, marginBottom: '0.75rem' }}>
              Select a table to place at grid position <strong>{assigningSlot.gridAddress}</strong>
            </p>
            {unassignedTables.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: TEXT_MUTED, fontSize: '0.8rem' }}>
                All tables are already assigned to grid slots.
              </div>
            ) : (
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: `1px solid ${BORDER_LIGHT}`, borderRadius: '4px' }}>
                {unassignedTables.map((t) => (
                  <div
                    key={t.id}
                    style={styles.tableOption}
                    onClick={() => handleAssign(t.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleAssign(t.id)}
                  >
                    <span>
                      <strong>T{t.tableNumber}</strong> — {t.tableName}
                    </span>
                    <span style={{ fontSize: '0.7rem', color: TEXT_MUTED }}>
                      {t.zone.name} ({t.capacity} seats)
                    </span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
              <button
                style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                onClick={() => setAssigningSlot(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Popup for Assigned Slots */}
      {actionSlot && (actionSlot.table || actionSlot.tableId) && (
        <div style={styles.modalOverlay as React.CSSProperties} onClick={() => setActionSlot(null)}>
          <div style={{ ...styles.modal as React.CSSProperties, width: '320px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '0.5rem' }}>
              {actionSlot.gridAddress}
            </div>
            <div style={{ fontSize: '0.82rem', color: TEXT_MUTED, marginBottom: '1rem' }}>
              {actionSlot.table
                ? `Table ${actionSlot.table.tableNumber} — ${actionSlot.table.tableName}`
                : 'Table assigned (loading details...)'}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              {actionSlot.table && (
                <button
                  onClick={() => { onSelectTable(actionSlot.table!.id); setActionSlot(null); }}
                  style={{ padding: '0.5rem 1rem', fontSize: '0.82rem', fontWeight: 600, backgroundColor: PRIMARY, color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  View Table
                </button>
              )}
              <button
                onClick={async () => { await handleUnassign(actionSlot); setActionSlot(null); }}
                style={{ padding: '0.5rem 1rem', fontSize: '0.82rem', fontWeight: 600, backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Unassign
              </button>
              <button
                onClick={() => { setActionSlot(null); setAssigningSlot(actionSlot); }}
                style={{ padding: '0.5rem 1rem', fontSize: '0.82rem', fontWeight: 600, backgroundColor: '#1565c0', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Reassign
              </button>
              <button
                onClick={() => setActionSlot(null)}
                style={{ padding: '0.5rem 1rem', fontSize: '0.82rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
